import React from 'react';
import Drawer from '@mui/material/Drawer';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HistoryIcon from '@mui/icons-material/History';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { PHONE_QUERY, useMediaQuery } from '../ui';
import type { MachineKind } from './machineControlTypes';

export type MachineContextMenuTarget = {
  mouseX: number;
  mouseY: number;
  macId: string;
  name: string;
  kind: MachineKind;
  isOutOfOrder: boolean;
  statusLabel?: string;
};

type Props = {
  target: MachineContextMenuTarget | null;
  busy?: boolean;
  onClose: () => void;
  onStart: (target: MachineContextMenuTarget) => void;
  onStop: (target: MachineContextMenuTarget) => void;
  onChangeCycle: (target: MachineContextMenuTarget) => void;
  onToggleOutOfOrder: (target: MachineContextMenuTarget) => void;
  onViewErrorHistory: (target: MachineContextMenuTarget) => void;
};

const MachineContextMenu: React.FC<Props> = ({
  target,
  busy,
  onClose,
  onStart,
  onStop,
  onChangeCycle,
  onToggleOutOfOrder,
  onViewErrorHistory,
}) => {
  const isPhone = useMediaQuery(PHONE_QUERY);
  const hasMacId = !!target?.macId;
  const knownKind = target?.kind === 'washer' || target?.kind === 'dryer';

  const header = target ? (
    <div className="sq-machine-menu-head">
      <Typography component="div" sx={{ fontWeight: 650, fontSize: isPhone ? '1rem' : '0.8125rem' }}>
        {target.name || target.macId}
      </Typography>
      {target.statusLabel ? (
        <Typography component="div" variant="caption" color="text.secondary">
          {target.statusLabel}
        </Typography>
      ) : null}
    </div>
  ) : null;

  const items = [
    <MenuItem
      key="start"
      disabled={!hasMacId || busy || !knownKind}
      onClick={() => target && onStart(target)}
    >
      <ListItemIcon>
        <PlayArrowIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Start</ListItemText>
    </MenuItem>,
    <MenuItem key="stop" disabled={!hasMacId || busy} onClick={() => target && onStop(target)}>
      <ListItemIcon>
        <StopIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Stop</ListItemText>
    </MenuItem>,
    <MenuItem
      key="cycle"
      disabled={!hasMacId || busy || !knownKind}
      onClick={() => target && onChangeCycle(target)}
    >
      <ListItemIcon>
        <SyncAltIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Change cycle</ListItemText>
    </MenuItem>,
    <Divider key="d1" />,
    <MenuItem
      key="ooo"
      disabled={!hasMacId || busy}
      onClick={() => target && onToggleOutOfOrder(target)}
    >
      <ListItemIcon>
        {target?.isOutOfOrder ? (
          <CheckCircleOutlineIcon fontSize="small" />
        ) : (
          <BuildIcon fontSize="small" />
        )}
      </ListItemIcon>
      <ListItemText>{target?.isOutOfOrder ? 'Clear out of order' : 'Mark out of order'}</ListItemText>
    </MenuItem>,
    <Divider key="d2" />,
    <MenuItem key="history" disabled={!hasMacId} onClick={() => target && onViewErrorHistory(target)}>
      <ListItemIcon>
        <HistoryIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>View error history</ListItemText>
    </MenuItem>,
  ];

  // A pointer-anchored menu lands under the thumb and gets clipped on phones.
  if (isPhone) {
    return (
      <Drawer anchor="bottom" open={!!target} onClose={onClose}>
        {header}
        <MenuList autoFocusItem={!!target} sx={{ pb: 1 }}>
          {items}
        </MenuList>
      </Drawer>
    );
  }

  return (
    <Menu
      open={!!target}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={target ? { top: target.mouseY, left: target.mouseX } : undefined}
      slotProps={{ paper: { sx: { minWidth: 220 } } }}
    >
      {header}
      {items}
    </Menu>
  );
};

export default MachineContextMenu;
